import * as React from 'react'
import { FullScreenDialog } from '@user/components/FullScreenDialog'
import {
  useActions,
  useData,
  useLeadAutoCloseIndicator,
  useLeadAutoCloseSignedFlow,
} from './hooks'
import { ENTITY_TYPES } from '@sales-center/common'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import { Formik } from 'formik'
import { AgreementFormPacket } from '@user/components/Agreements/AgreementFormPacketDialog/AgreementFormPacket'
import { SettingsPage } from '@sales-center/components'
import { useTheme } from '@mui/material/styles'
import {
  EmbedFormManagerDrawer,
  FormActions,
} from '@user/components/Agreements/EmbedFormManagerDrawer'
import { Scrollbar } from '@workwave-tidal/core/components'
import { useGetEntityDetailsOnMount } from './hooks/useLeadOrOppDetails'
import { AgreementSource } from '@user/store/agreements'

interface Props {
  onClose: () => void
  onCloseFormManager: () => void
  open: boolean
  entityId: number
  entityType: ENTITY_TYPES
  openServicesDrawer: () => void
  cancelPopupOpen: boolean
  handleCancel: () => void
  cancelPopupClose: () => void
  source?: AgreementSource
  onLeadAutoClosed?: () => void
}

export function AgreementFormPacketDialog(props: Props) {
  const {
    open,
    onClose,
    entityId,
    entityType,
    onCloseFormManager,
    onLeadAutoClosed,
    source = 'detail',
  } = props

  const { success, failed, loading, initialValues } = useData(props)
  const theme = useTheme()

  useGetEntityDetailsOnMount(entityId, entityType)

  const {
    onValidate,
    onSubmit,
    formId,
    editDrawerOpen,
    closeEditDrawer,
    onPreview,
  } = useActions(entityId, entityType, onCloseFormManager, source)

  const { showAutoCloseLeadBanner } = useLeadAutoCloseIndicator(
    entityId,
    entityType,
  )
  const { onFormStatusChange, handleFormManagerClosed } =
    useLeadAutoCloseSignedFlow(
      entityId,
      showAutoCloseLeadBanner,
      onLeadAutoClosed,
    )

  const handleCloseFormManager = React.useCallback(() => {
    closeEditDrawer()
    handleFormManagerClosed()
  }, [closeEditDrawer, handleFormManagerClosed])

  return (
    <FullScreenDialog open={open} onClose={onClose} closeButtonText="Cancel">
      <SettingsPage
        padding="0px"
        fullScreen
        contentHeight={'100%'}
        bgColor={theme.palette.background.default}
      >
        <Scrollbar>
          <Stack alignItems="center">
            {loading && (
              <Box p={2} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            )}

            {success && (
              <Formik
                onSubmit={onSubmit}
                initialValues={initialValues}
                validateOnChange={true}
                validate={(values) => onValidate(values)}
              >
                <AgreementFormPacket {...props} onPreview={onPreview} />
              </Formik>
            )}

            {failed && (
              <Box p={2}>
                <Alert severity="error">
                  {' '}
                  Failed to load agreement templates.{' '}
                </Alert>
              </Box>
            )}

            {!!formId && (
              <EmbedFormManagerDrawer
                action={FormActions.Edit}
                formId={formId}
                onClose={handleCloseFormManager}
                onFormStatusChange={onFormStatusChange}
                open={editDrawerOpen}
              />
            )}
          </Stack>
        </Scrollbar>
      </SettingsPage>
    </FullScreenDialog>
  )
}
